import React from "react";
import type { PlannerFillMode } from "./types";

type IconProps = { className?: string };

/** Plus glyph of the "Add keyword" / "Add geo" buttons — inherits currentColor. */
export const PlusIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M10 4.6v10.8M4.6 10h10.8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Chevron of the country / order-type selects. */
export const ChevronDownIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M6 8.5 10 12.5l4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Trash can of the row / block remove buttons. */
export const TrashIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M3.3 5.5h13.4M8 3.2h4M4.8 5.5l.8 10.1a1.6 1.6 0 0 0 1.6 1.5h5.6a1.6 1.6 0 0 0 1.6-1.5l.8-10.1M8.2 8.6v5M11.8 8.6v5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Two sheets — copies a row's daily installs (same glyph as App Insights). */
export const CopyIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <rect
      x="7"
      y="7"
      width="10"
      height="10"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M13 7V5.5A2.5 2.5 0 0 0 10.5 3h-5A2.5 2.5 0 0 0 3 5.5v5A2.5 2.5 0 0 0 5.5 13H7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Clipboard — pastes the copied installs into a row. */
export const PasteIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M7.5 4H6.2a1.7 1.7 0 0 0-1.7 1.7v10.1a1.7 1.7 0 0 0 1.7 1.7h7.6a1.7 1.7 0 0 0 1.7-1.7V5.7A1.7 1.7 0 0 0 13.8 4h-1.3M7.5 10.5h5M7.5 13.5h3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect
      x="7.5"
      y="2.5"
      width="5"
      height="3"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

/** Rocket-less "create" arrow of the block footer button. */
export const CreateOrderIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M4.2 10h11.6M11 5.2 15.8 10 11 14.8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Warning triangle shown next to a block-level validation message. */
export const AlertIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M10 7.1v3.4M10 13.4h.01M8.6 3.6 2.4 14.2a1.6 1.6 0 0 0 1.4 2.4h12.4a1.6 1.6 0 0 0 1.4-2.4L11.4 3.6a1.6 1.6 0 0 0-2.8 0Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Indeterminate spinner of a block whose order is being prepared. */
export const SpinnerIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle
      cx="10"
      cy="10"
      r="7"
      stroke="currentColor"
      strokeOpacity="0.3"
      strokeWidth="2"
    />
    <path
      d="M17 10a7 7 0 0 0-7-7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/** Pencil of the plan tab's "Rename" button. */
export const PencilIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M11.9 4.9l3.2 3.2M4.4 15.6l.6-3.4 8.3-8.3a1.6 1.6 0 0 1 2.3 0l.5.5a1.6 1.6 0 0 1 0 2.3l-8.3 8.3-3.4.6z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Crossed arrows — draws the Random fill of a row again. */
export const ShuffleIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M3.5 14h1.9a3 3 0 0 0 2.5-1.3l3.7-5.4A3 3 0 0 1 14.1 6h2.4M3.5 6h1.9a3 3 0 0 1 2.4 1.2M16.5 14h-2.4a3 3 0 0 1-2.4-1.2M14.5 4l2 2-2 2M14.5 12l2 2-2 2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Bar heights of each fill glyph, left to right, out of 12. */
const FILL_MODE_BARS: Record<PlannerFillMode, number[]> = {
  fixed: [7, 7, 7, 7],
  "random-increase": [3, 6, 9, 12],
  "random-decrease": [12, 9, 6, 3],
};

/**
 * Four bars drawing the shape a fill mode gives the days. A mode it doesn't
 * know (a plan saved by another version) draws as Fixed rather than failing.
 */
export const FillModeIcon: React.FC<IconProps & { mode: PlannerFillMode }> = ({
  className,
  mode,
}) => (
  <svg
    className={className}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    {(FILL_MODE_BARS[mode] ?? FILL_MODE_BARS.fixed).map((height, index) => (
      <rect
        key={index}
        x={1.5 + index * 3.5}
        y={14 - height}
        width="2.5"
        height={height}
        rx="0.75"
      />
    ))}
  </svg>
);
